import JSZip from 'jszip';
import { getRoomShortName } from './RoomConfig';

class LayoutSaver {
  constructor(layoutRef) {
    this.layoutRef = layoutRef;
  }

  async loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  }

  async svgToImage(svgElement) {
    // Deep clone the SVG
    const svgClone = svgElement.cloneNode(true);
    
    // Get the image element and its dimensions
    const imageElement = svgElement.querySelector('image');
    const width = parseFloat(imageElement.getAttribute('width'));
    const height = parseFloat(imageElement.getAttribute('height'));
    const backgroundHref = imageElement.getAttribute('href');
    
    // Set SVG dimensions to match image
    svgClone.setAttribute('width', width);
    svgClone.setAttribute('height', height);
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    // Convert SVG to string
    const svgString = new XMLSerializer().serializeToString(svgClone);
    const svgUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
    
    try {
      // Create canvas with image dimensions
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // Clear canvas with white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // First load and draw the background image
      if (backgroundHref) {
        try {
          const bgImage = await this.loadImage(backgroundHref);
          ctx.drawImage(bgImage, 0, 0, canvas.width, canvas.height);
        } catch (error) {
          console.error('Error loading background image:', error);
        }
      }

      // Then load and draw the SVG content
      const svgImage = await this.loadImage(svgUrl);
      ctx.drawImage(svgImage, 0, 0, canvas.width, canvas.height);

      return canvas;
    } catch (error) {
      console.error('Error in svgToImage:', error);
      throw error;
    }
  }

  async captureLayout() {
    if (!this.layoutRef.current) return null;

    const svgElement = this.layoutRef.current.querySelector('svg');
    if (!svgElement) return null;

    try {
      return await this.svgToImage(svgElement);
    } catch (error) {
      console.error('Capture error:', error);
      throw error;
    }
  }

  getRoomName(roomId) {
    return getRoomShortName(roomId);
  }

  async saveAllLayouts(roomIds, setSelectedRoom) {
    const zip = new JSZip();
    const originalRoom = roomIds[0];

    try {
      for (const roomId of roomIds) {
        setSelectedRoom(roomId);
        
        // Wait for room to render
        await new Promise(resolve => setTimeout(resolve, 1500));

        try {
          const canvas = await this.captureLayout();
          if (canvas) {
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
            zip.file(`${this.getRoomName(roomId)}-layout.png`, blob);
          }
        } catch (error) {
          console.error(`Error capturing ${this.getRoomName(roomId)}:`, error);
        }
      }

      const content = await zip.generateAsync({ type: 'blob' });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(content);
      link.download = 'room-layouts.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error('Error saving layouts:', error);
      throw new Error('Failed to save layouts');
    } finally {
      setSelectedRoom(originalRoom);
    }
  }
}

export default LayoutSaver;